import { calendarDateToTimestamp } from "@shared/formats/calendar-date";

export function formatDate(
  date: Date | number | string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  };

  return dateObj.toLocaleDateString("en-US", defaultOptions);
}

export function formatTime(
  date: Date | number | string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...options,
  };

  return dateObj.toLocaleTimeString("en-US", defaultOptions);
}

export function formatDateTime(date: Date | number | string): string {
  return `${formatDate(date)}, ${formatTime(date, { timeZoneName: "short" })}`;
}

export function formatDateTimeUtc(date: Date | number | string): string {
  return `${formatDate(date, { timeZone: "UTC", hour: "numeric", minute: "2-digit" })} UTC`;
}

export function formatDateForInput(date: Date | number | string): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return dateObj.toISOString().split("T")[0];
}

export function formatDateTimeForInput(date: Date | number | string): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return dateObj.toISOString().slice(0, 16);
}

export function formatStripeDate(stripeTimestamp: number): string {
  return formatDate(stripeTimestamp * 1000);
}

export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
}

export function parseDateInput(dateStr: string): number {
  const date = new Date(dateStr + "T00:00:00");
  return date.getTime();
}

export function formatShortDate(date: Date | number | string): string {
  const parsed = new Date(date);
  const now = new Date();

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: parsed.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export function formatDateSafe(
  date: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const parsed =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    ...options,
  }).format(parsed);
}

function calendarDayTimestamp(date: number | string): number {
  return typeof date === "string" ? calendarDateToTimestamp(date) : date;
}

export function formatCalendarDay(date: number | string): string {
  return formatDateSafe(calendarDayTimestamp(date), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCalendarWeekday(date: number | string): string {
  return formatDateSafe(calendarDayTimestamp(date), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatCalendarDayLong(date: number | string): string {
  return formatDateSafe(calendarDayTimestamp(date), {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatMonthYear(date: Date | number | string): string {
  const parsed =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

export function formatDateTimeCompact(date: Date | number | string): string {
  const parsed = new Date(date);
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function formatCompactDate(date: Date | number | string): string {
  const parsed = new Date(date);
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
}

export function formatConversationTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (dateDay.getTime() === today.getTime()) return time;
  if (dateDay.getTime() === yesterday.getTime()) return `Yesterday, ${time}`;

  const includeYear = date.getFullYear() !== now.getFullYear();
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
  return `${dateStr}, ${time}`;
}

export function formatYearMonth(
  yearMonth: string | null,
  options: Pick<Intl.DateTimeFormatOptions, "month" | "year"> = {
    month: "long",
    year: "numeric",
  },
): string {
  if (!yearMonth) return "";
  const [year, month] = yearMonth.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString("en-US", options);
}

export function groupActivityByDay<TItem>(
  items: ReadonlyArray<TItem>,
  timestampOf: (item: TItem) => number,
): Array<{ day: string; items: TItem[] }> {
  const groups: Array<{ day: string; items: TItem[] }> = [];
  for (const item of items) {
    const day = formatDate(timestampOf(item));
    const current = groups.at(-1);
    if (current?.day === day) current.items.push(item);
    else groups.push({ day, items: [item] });
  }
  return groups;
}

export function formatMonthDayTime(date: number): string {
  return `${formatDate(date, { year: undefined })}, ${formatTime(date)}`;
}
